import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import UserFileServices from 'services/userFile.service';
import DownloadService from 'services/download.service';
import UploadService from 'services/upload.service';

import { IUserFile } from 'models/UserFile';
import { IBatchRequestResponse } from 'models/Batch';

import fileImg from 'img/file.svg';
import excludeImg from 'img/exclude.svg';
import editImg from 'img/edit.svg';
import optionsImg from 'img/icon-detalhes.svg';
import optionsInnerImg from 'img/icon-detalhes-no-border.svg';

import { Grid, Column, Row } from 'components/Layout/Grid';
import Text from 'components/Text';
import { Button } from 'components/Button';
import Input from 'components/Input';
import toast from 'components/Toast/dispatch';
import Paginator from 'components/Paginator';
import Loading from 'components/Loading';
import FileDropZone from 'components/FileDropZone';
import { useCustomEvent } from 'components/ApplicationInsights';
import { eventName } from 'components/ApplicationInsights/eventNames';

import { useWindowSize } from 'hooks/useWindowSize';
import { GetDateToServer } from 'utils/TimeOutput';
import DateOutput, { DateOutputStr } from 'utils/DateOutput';

import { FileStyles } from './styles';

const Files: React.FC = () => {
  const customer = useSelector((state: RootState) => state.customer);
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const [files, setFiles] = useState<IBatchRequestResponse<IUserFile>>();
  const [currentPage, setCurrentPage] = useState(0);

  const [pageSize, setPageSize] = useState(4);

  const _userFileService = new UserFileServices();
  const _uploadService = new UploadService();

  const pageSizeWidth = () => {
    if (windowSize.width >= 670)
      return 4;

    return windowSize.width >= 500 ? 3 : 2;
  };

  useEffect(() => {
    setPageSize(pageSizeWidth());
  }, [windowSize]);

  const getFiles = async () => {
    setFiles(undefined);

    await _userFileService
      .get(currentPage, pageSize, 'createdDate desc')
      .then((data) => setFiles(data));
  };

  useEffect(() => {
    getFiles();
  }, [currentPage, customer]);

  const FileItem = (data: IUserFile) => {
    const [isDeletingFile, setIsDeletingFile] = useState(false);

    function getItemTypeByUrl(): string {
      if (!data.fileUrl) return '-';

      const getLength = data.fileUrl.split('.').length;

      return getLength ? data.fileUrl.split('.')[getLength - 1] : '-';
    }

    async function deleteFile() {
      setIsDeletingFile(true);

      await _userFileService
        .delete(data?.id || '')
        .then(async () => {
          toast.success('Arquivo excluido com sucesso!');
          currentPage > 0 ? setCurrentPage(0) : await getFiles();
        })
        .catch(() => toast.error('Erro ao exluir arquivo.'));

      setIsDeletingFile(false);
    }

    return (
      <FileStyles>
        {isDeletingFile && <Loading size={45} />}
        <Row className="file-options animated slideInDown">
          <img src={optionsImg} alt="" />
          <Column className="file-options-content">
            <Row className="justify-start">
              <img src={optionsInnerImg} alt="" />
            </Row>
            <Column className="pl-3">
              <Text size={12}>
                Incluído: <DateOutput dateStr={data.createdDate} />
              </Text>
              <Text size={12}>
                Vigência:{' '}
                {data.dueDate && (
                  <DateOutput dateStr={data.dueDate} patternStr="dd/MM/yyyy" />
                )}
              </Text>
              <Text size={12}>Por: {data.fullName}</Text>
            </Column>
            <Button
              onClick={() =>
                dispatch({
                  type: 'OPEN_SLIDER',
                  data: {
                    title: 'Editar Arquivo',
                    content: FileSlider,
                    state: data,
                  },
                })
              }
              className="pl-3 mt-1 flex-1 justify-start"
              style={{ borderTop: '1px solid #A0A0A0', borderRadius: 0 }}
            >
              <>
                <img src={editImg} alt="" />
                <Text className="ml-2 color-blue-gray" size={12}>
                  Editar
                </Text>
              </>
            </Button>
            <Button
              onClick={deleteFile}
              className="pl-3 flex-1 justify-start"
              style={{
                background:
                  'linear-gradient(180deg, transparent 0%, #DDDBDB 100%)',
                borderTop: '1px solid #A0A0A0',
                borderRadius: 0,
              }}
            >
              <>
                <img src={excludeImg} alt="" />
                <Text className="ml-2 color-blue-gray" size={12}>
                  Excluir
                </Text>
              </>
            </Button>
          </Column>
        </Row>
        <Column className="file-portrait relative">
          <img
            className="cursor-pointer"
            src={fileImg}
            alt=""
            onClick={() => DownloadService.CreateDownloadLink(data.fileUrl)}
            role="presentation"
          />
          <Row
            className="absolute bg-warning align-center justify-center px-1"
            style={{ borderRadius: 4, left: '34%', bottom: '30px' }}
          >
            <Text className="color-white font-bold" size={10}>
              {getItemTypeByUrl().toUpperCase()}
            </Text>
          </Row>
        </Column>
        <Column className="file-description align-center justify-center">
          <Text className="text-wrap" size={11}>
            {data.name}
          </Text>
        </Column>
      </FileStyles>
    );
  };

  const FileSlider = () => {
    const componentName = `Menu`;
    const trackMenuClick = useCustomEvent(componentName, eventName.ADD_ARQUIVO_DASH_CLIENTE);
    const sliderState = useSelector((state: RootState) => state?.slider?.state);
    const slider = useSelector((state: RootState) => state.slider);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>({
      file: undefined,
      name: '',
      dueDate: '',
    });

    useEffect(() => {
      if (slider?.title) {
        trackMenuClick({ item: slider?.title });
      }
    }, [slider]);

    useEffect(() => {
      if (sliderState) {
        setData({
          ...sliderState,
          dueDate: DateOutputStr(sliderState.dueDate, 'yyyy-MM-dd'),
        });
      }
    }, []);

    async function uploadFile() {
      return _uploadService.upload(data.file);
    }

    async function saveFile(result: any) {
      await _userFileService
        .create({
          file: result.id,
          name: data.name,
          customerId: customer?.id,
          dueDate: GetDateToServer(data.dueDate),
        })
        .then(() => {
          toast.success('Arquivo enviado com sucesso!');
          getFiles();
          setLoading(false);
          dispatch({ type: 'CLOSE_SLIDER' });
        })
        .catch(() => {
          setLoading(false);
          return toast.error('Erro ao enviar arquivo.');
        });
    }

    async function updateFile(result: any) {
      await _userFileService
        .update({
          ...sliderState,
          file: result.id,
          name: data.name,
          customerId: customer?.id,
          dueDate: GetDateToServer(data.dueDate),
        })
        .then(() => {
          toast.success('Arquivo atualizado com sucesso!');
          getFiles();
          setLoading(false);
          dispatch({ type: 'CLOSE_SLIDER' });
        })
        .catch(() => {
          setLoading(false);
          return toast.error('Erro ao atualizar arquivo.');
        });
    }

    async function onSubmit() {
      setLoading(true);

      if (typeof data.file !== 'string') {
        await uploadFile()
          .then((result) => saveFile(result))
          .catch(() => {
            setLoading(false);
            return toast.error('Erro ao fazer upload do arquivo.');
          });
      } else {
        await updateFile({ id: data.file });
      }
    }

    return (
      <Column className="w-100 mt-3">
        <FileDropZone data={data} setData={setData} />
        <Column className="flex-1 mt-3">
          <Text className="color-blue-gray mb-1" size={12}>
            Nome do arquivo*
          </Text>
          <Input
            onChange={(e: any) => setData({ ...data, name: e.target.value })}
            className="w-100"
            type="text"
            value={data.name}
          />
        </Column>
        <Row className="flex-md-column mt-3">
          <Column className="flex-1 mr-3 mr-md-0 mb-md-2">
            <Text className="color-blue-gray mb-1 mb-md-2" size={12}>
              Vigência
            </Text>
            <Input
              required
              onChange={(e: any) =>
                setData({ ...data, dueDate: e.target.value })
              }
              type="date"
              name="Vigência"
              value={data.dueDate}
              className="min-width-input"
            />
          </Column>
          <Column className="flex-2" />
        </Row>
        <Row className="mt-3 align-center justify-center flex-1">
          <Button
            onClick={onSubmit}
            className="outline"
            isRequesting={loading}
            spinnerColorClass="color-primary"
            width="auto"
          >
            <Text className="font-bold color-primary">Gravar Arquivo</Text>
          </Button>
        </Row>
      </Column>
    );
  };

  return (
    <Column className="mt-2 customer-file-wrapper">
      <Row className="flex-1 justify-between">
        <Text className="color-primary font-bold mb-1" size={24}>
          Arquivos
        </Text>

      </Row>
      <Column className="flex-1 align-center relative">
        <Grid
          className="w-100"
          cols={pageSize}
          colsGap="20px"
          rows={1}
          style={{ height: 120 }}
        >
          {files?.data?.map((file, index) => (
            <FileItem key={index} {...file} />
          )) || <Loading size={45} />}
        </Grid>
        {files && files?.totalPages > 0 ? (
          <Paginator
            onPageChange={setCurrentPage}
            totalPage={files?.totalPages}
            currentPage={currentPage}
            pageSize={pageSize}
            minifyed
          />
        ) : null}
      </Column>
    </Column>
  );
};

export default Files;
